import React, { useEffect, useRef, useState } from "react";
import TaskRow from "./TaskRow";
import TaskEditSheet from "./TaskEditSheet";
import UndoToast from "../ui/UndoToast";
import useUndoDelete from "../ui/useUndoDelete";
import { summarize } from "./taskSummary";
import "../ui/tokens.css";
import "./tasks.css";

// My tasks, framed as small steps: a calm summary, an add box, what is left and what is done.
export default function TasksScreen({ tasks, newTask, onNewTaskChange, onAdd, onToggle, onEdit, onDelete, onBreakDown, onAskRigby, onBack }) {
  const inputRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const undo = useUndoDelete((id) => {
    onDelete(id);
    return true;
  });
  const { forget } = undo;

  useEffect(() => {
    forget(tasks.map((t) => t.id));
  }, [tasks, forget]);

  const visible = tasks.filter((t) => !undo.hidden.includes(t.id));
  const todo = visible.filter((t) => !t.completed);
  const done = visible.filter((t) => t.completed);
  const summary = summarize(visible);
  const closeEdit = () => setEditing(null);

  const addBox = (
    <form
      className="tk-add"
      onSubmit={(e) => {
        e.preventDefault();
        onAdd();
      }}
    >
      <input
        ref={inputRef}
        className="tk-add-input"
        type="text"
        value={newTask}
        onChange={(e) => onNewTaskChange(e.target.value)}
        placeholder="Add a small step"
        aria-label="Add a small step"
        autoComplete="off"
        enterKeyHint="done"
      />
      <button type="submit" className="tk-plus" aria-label="Add task">
        <span aria-hidden="true" />
      </button>
    </form>
  );

  return (
    <div className="tk">
      <header className="tk-bar">
        <button type="button" className="tk-back" onClick={onBack}>
          Chat
        </button>
        <h1 className="tk-title">My tasks</h1>
        <span className="tk-bar-space" />
      </header>

      <div className="tk-scroll">
        <div className="tk-col">
          {summary.total > 0 ? (
            <section className="tk-hero" aria-label="Progress">
              <div className="tk-ring" style={{ background: `conic-gradient(var(--rg-accent) 0 ${summary.percent * 3.6}deg, var(--rg-border-strong) ${summary.percent * 3.6}deg)` }}>
                <span>
                  {summary.done}/{summary.total}
                </span>
              </div>
              <div className="tk-hero-text">
                <h2>{summary.headline}</h2>
                <p>{summary.sub}</p>
              </div>
            </section>
          ) : null}

          {addBox}

          {summary.total === 0 ? (
            <div className="tk-empty">
              <div className="tk-orb" />
              <h2>Nothing on your plate.</h2>
              <p>Add a small step, or tell Rigby what is on your mind and it can suggest one.</p>
              <div className="tk-empty-actions">
                <button type="button" className="tk-chip" onClick={() => inputRef.current && inputRef.current.focus()}>
                  Add a small step
                </button>
                <button type="button" className="tk-chip" onClick={onAskRigby}>
                  Ask Rigby
                </button>
              </div>
            </div>
          ) : null}

          {todo.length > 0 ? (
            <section aria-label="To do">
              <h3 className="tk-sec">
                <span>To do</span>
                <span>{todo.length}</span>
              </h3>
              {todo.map((t) => (
                <TaskRow key={t.id} task={t} onToggle={onToggle} onOpen={setEditing} onDelete={(task) => undo.request(task.id)} />
              ))}
            </section>
          ) : null}

          {done.length > 0 ? (
            <section aria-label="Done">
              <h3 className="tk-sec">
                <span>Done</span>
                <span>{done.length}</span>
              </h3>
              {done.map((t) => (
                <TaskRow key={t.id} task={t} onToggle={onToggle} onOpen={setEditing} onDelete={(task) => undo.request(task.id)} />
              ))}
            </section>
          ) : null}

          {summary.total > 0 ? <p className="tk-hint">Swipe left on a task to delete it</p> : null}
        </div>
      </div>

      <TaskEditSheet
        task={editing}
        onClose={closeEdit}
        onSave={(text) => {
          onEdit(editing.id, text);
          closeEdit();
        }}
        onBreakDown={(text) => {
          closeEdit();
          onBreakDown(text);
        }}
        onDelete={() => {
          undo.request(editing.id);
          closeEdit();
        }}
      />

      <UndoToast visible={undo.pendingId !== null} label="Task deleted" onUndo={undo.undo} />
    </div>
  );
}
